import { parseTime, formatTime } from "./timeParse";
import { looksLikeVideo } from "./utils";
import { mergeVideos } from "./operations";
import type { TrimMode } from "./operations";
import { useState, useCallback, useRef, useEffect } from "react";
import { nudgeFrame, nudgeSeconds, timeToPercent, pointerToTime, moveHandle, moveItem, outputLabel, actualStartText, round1 } from "./trimTimeline";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, execWithProgress, fetchFile, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine, trimVideoSegment } from "./operations";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { Plus, X, Scissors, ChevronUp, ChevronDown, Repeat } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Cuts one or more segments out of a video, each with its own start and end point, without needing a full video editor. Add as many segments as you like, download each one individually, or merge them all into a single combined clip. Runs in your browser: the video never leaves your device, but the first use downloads a processing engine from a public CDN.`,
  how: `Upload your video, add a segment (start and end time as seconds, m:ss or h:mm:ss) for each cut you want, and process them. Each segment downloads on its own, or use Merge All to combine every segment into one file in the order they're listed.`,
  tip: `"Exact" (the default) cuts at precisely the times you type and always produces a playable clip; it re-encodes, so it takes longer. "Fast" copies the video without re-encoding (identical quality, seconds) but can only start on a key frame, so the clip may begin up to a few seconds early; if that would leave no picture, the tool switches to an exact cut for that segment and tells you. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone's own share options first.`,
};

interface Segment {
  id: number;
  start: string;
  end: string;
  result: Blob | null;
  /** How this segment was actually cut ("exact" also when a fast cut fell back). */
  cutMode?: TrimMode;
  /** Fast cut: seconds before the requested start that the clip really begins. */
  earlyBy?: number;
}

let nextSegmentId = 1;

export default function TrimVideo() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [segments, setSegments] = useState<Segment[]>([{ id: 0, start: "0", end: "10", result: null }]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [merging, setMerging] = useState(false);
  const [mergedResult, setMergedResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [mode, setMode] = useState<TrimMode>("exact");
  const [duration, setDuration] = useState<number | null>(null);
  const [fellBackCount, setFellBackCount] = useState(0);
  const [unreadable, setUnreadable] = useState(false);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`); return; }
    setUnreadable(false);
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
    setSegments([{ id: 0, start: "0", end: "10", result: null }]);
    // The end of the first segment defaults to the real length of the video, not a fixed 10 s.
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.onloadedmetadata = () => {
      if (Number.isFinite(probe.duration) && probe.duration > 0) {
        setDuration(probe.duration);
        setSegments([{ id: 0, start: "0", end: String(Math.round(probe.duration * 10) / 10), result: null }]);
      }
    };
    probe.onerror = () => setUnreadable(true);
    probe.src = url;
    setMergedResult(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const addSegment = () => {
    setSegments(prev => [...prev, { id: nextSegmentId++, start: "0", end: duration ? String(Math.round(duration * 10) / 10) : "10", result: null }]);
    setMergedResult(null);
  };

  const removeSegment = (id: number) => {
    setSegments(prev => prev.filter(s => s.id !== id));
    setMergedResult(null);
  };

  const updateSegment = (id: number, field: "start" | "end", value: string) => {
    setSegments(prev => prev.map(s => s.id === id ? { ...s, [field]: value, result: null } : s));
    setMergedResult(null);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loopId, setLoopId] = useState<number | null>(null);
  const [recutting, setRecutting] = useState<number | null>(null);
  const activeSeg = segments.find(s => s.id === activeId) ?? segments[0];

  const setTimes = (id: number, which: "start" | "end", t: number) => {
    const seg = segments.find(s => s.id === id); if (!seg || !duration) return;
    const a = parseTime(seg.start), b = parseTime(seg.end);
    const start = Number.isFinite(a) ? a : 0, end = Number.isFinite(b) ? b : duration;
    const m = moveHandle(which, t, start, end, duration);
    setSegments(prev => prev.map(x => x.id === id ? { ...x, start: String(round1(m.start)), end: String(round1(m.end)), result: null } : x));
    setMergedResult(null);
  };

  const moveSegment = (index: number, delta: number) => {
    setSegments(prev => moveItem(prev, index, delta));
    setMergedResult(null);
  };

  const onHandlePointer = (e: React.PointerEvent<HTMLDivElement>, id: number, which: "start" | "end") => {
    if (busy || !duration || !barRef.current) return;
    if (e.type === "pointerdown") { e.currentTarget.setPointerCapture(e.pointerId); setActiveId(id); }
    else if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const rect = barRef.current.getBoundingClientRect();
    setTimes(id, which, pointerToTime(e.clientX, rect.left, rect.width, duration));
  };

  const onHandleKey = (e: React.KeyboardEvent<HTMLDivElement>, id: number, which: "start" | "end") => {
    if (busy || !duration) return;
    const seg = segments.find(s => s.id === id); if (!seg) return;
    const cur = parseTime(which === "start" ? seg.start : seg.end);
    if (!Number.isFinite(cur)) return;
    let next: number | null = null;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = e.shiftKey ? nudgeSeconds(cur, -1, duration) : nudgeFrame(cur, -1, duration);
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = e.shiftKey ? nudgeSeconds(cur, 1, duration) : nudgeFrame(cur, 1, duration);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = duration;
    if (next === null) return;
    e.preventDefault(); e.stopPropagation();
    setTimes(id, which, next);
    if (videoRef.current) videoRef.current.currentTime = next;
  };

  /** I / O set the active segment's start / end; arrows nudge the player by a frame (Shift: 1 s). */
  const onPlayerKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON" || e.ctrlKey || e.metaKey || e.altKey) return;
    const v = videoRef.current; if (!v || !duration) return;
    const k = e.key.toLowerCase();
    if (k === "i" && activeSeg) { e.preventDefault(); setFromPlayer(activeSeg.id, "start"); }
    else if (k === "o" && activeSeg) { e.preventDefault(); setFromPlayer(activeSeg.id, "end"); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const dir = e.key === "ArrowLeft" ? -1 : 1;
      v.currentTime = e.shiftKey ? nudgeSeconds(v.currentTime, dir, duration) : nudgeFrame(v.currentTime, dir, duration);
    }
  };

  // Loop preview: play start..end on repeat until stopped.
  useEffect(() => {
    const v = videoRef.current;
    if (loopId === null || !v) return;
    const seg = segments.find(s => s.id === loopId);
    const a = seg ? parseTime(seg.start) : NaN, b = seg ? parseTime(seg.end) : NaN;
    if (!(b > a)) { setLoopId(null); return; }
    v.currentTime = a;
    void v.play().catch(() => setLoopId(null));
    const tick = () => { if (v.currentTime >= b || v.currentTime < a - 0.5) v.currentTime = a; };
    v.addEventListener("timeupdate", tick);
    return () => { v.removeEventListener("timeupdate", tick); v.pause(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopId, segments.find(s => s.id === loopId)?.start, segments.find(s => s.id === loopId)?.end]);
  /** Puts the player's current time into a segment's start or end box. */
  const setFromPlayer = (id: number, field: "start" | "end") => {
    const t = videoRef.current?.currentTime;
    if (t === undefined) return;
    updateSegment(id, field, String(Math.round(t * 10) / 10));
  };

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  /** One plain sentence per segment that can't be cut yet, shown under its boxes. */
  const segmentError = (s: Segment): string | null => {
    const start = parseTime(s.start), end = parseTime(s.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return "Type the times as seconds (65.5), minutes:seconds (1:05) or hours:minutes:seconds.";
    if (start < 0) return "The start can't be negative.";
    if (end <= start) return end === start ? "Start and end are the same, so the clip would be empty." : "The end must come after the start.";
    if (duration && start >= duration) return `This starts at ${formatTime(start)} but the video is only ${formatTime(duration)} long.`;
    return null;
  };
  const anySegmentInvalid = segments.some((s) => segmentError(s) !== null);

  const validateSegments = (): boolean => {
    for (const s of segments) {
      const start = parseTime(s.start), end = parseTime(s.end);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end <= start) {
        toast.error("Every segment needs a valid start and end time (end must be after start)");
        return false;
      }
      if (duration && start >= duration) {
        toast.error(`A segment starts at ${start}s but the video is only ${duration.toFixed(1)}s long`);
        return false;
      }
    }
    return true;
  };

  const processAllSegments = async () => {
    if (!file || segments.length === 0) return;
    if (!validateSegments()) return;
    setProcessing(true);
    setProgress(0);
    setMergedResult(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      const results: Blob[] = [];
      const metas: { fellBack: boolean; early?: number }[] = [];
      let fell = 0;
      let early = 0;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        setStatusMessage(`Trimming segment ${i + 1} of ${segments.length}...`);
        const out = await trimVideoSegment(
          ffmpeg,
          `input.${ext}`,
          `segment_${i}`,
          String(parseTime(seg.start)),
          String(parseTime(seg.end)),
          (f) => setProgress((i + f) / segments.length),
          controller.signal,
          mode,
          ext,
          duration ? Math.max(300, (file.size * 8) / duration / 1000 * 0.9 - 128) : 0
        );
        metas.push({ fellBack: out.fellBack, early: out.startedEarlyBy });
        if (out.fellBack) fell++;
        if (out.startedEarlyBy && out.startedEarlyBy >= 0.3) early = Math.max(early, out.startedEarlyBy);
        results.push(ffmpegOutputToBlob(out.data, out.extension === "mp4" ? "video/mp4" : file.type || "video/mp4"));
      }
      setFellBackCount(fell);

      setSegments(prev => prev.map((s, i) => ({ ...s, result: results[i], cutMode: metas[i].fellBack ? "exact" : mode, earlyBy: metas[i].early })));
      toast.success(`${segments.length} segment(s) trimmed!` + (fell ? ` ${fell} needed an exact cut because a fast cut would have had no picture.` : "") + (mode === "exact" || fell ? " Saved as MP4." : ` Saved in the original format (.${ext}), starting at the nearest earlier key frame${early ? ` (up to about ${early} s before the time you chose)` : ""}.`));
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  /** Redoes one segment in Exact mode (e.g. after a Fast cut started early). */
  const recutExact = async (id: number) => {
    const seg = segments.find(s => s.id === id);
    if (!file || !seg || recutting !== null) return;
    setRecutting(id); setProgress(0); setMergedResult(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));
      setStatusMessage("Re-cutting exactly...");
      const out = await trimVideoSegment(ffmpeg, `input.${ext}`, "recut", String(parseTime(seg.start)), String(parseTime(seg.end)), setProgress, controller.signal, "exact", ext, duration ? Math.max(300, (file.size * 8) / duration / 1000 * 0.9 - 128) : 0);
      const blob = ffmpegOutputToBlob(out.data, "video/mp4");
      setSegments(prev => prev.map(s => s.id === id ? { ...s, result: blob, cutMode: "exact", earlyBy: undefined } : s));
      toast.success("Segment re-cut exactly. Saved as MP4.");
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setRecutting(null);
    }
  };

  const mergeAndDownload = async () => {
    const processedSegments = segments.filter(s => s.result);
    if (processedSegments.length < 2) {
      toast.error("Process at least 2 segments before merging");
      return;
    }
    setMerging(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      const clips = processedSegments.map((seg, i) => new File([seg.result!], `segment${i + 1}.mp4`, { type: "video/mp4" }));
      const out = await mergeVideos(ffmpeg, clips, setProgress, controller.signal, setStatusMessage);
      setMergedResult(ffmpegOutputToBlob(out.data, "video/mp4"));
      toast.success("Segments merged!");
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setMerging(false);
    }
  };

  const allProcessed = segments.length > 0 && segments.every(s => s.result);
  const busy = processing || merging || recutting !== null;

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" maxSize={5000} onFiles={handleFiles} label="Drop a video to trim" description="Cut one or more segments from a video" />
      ) : (
        <div className="space-y-4">
          <div onKeyDown={onPlayerKey} className="space-y-1">
            <video ref={videoRef} src={preview} controls onError={() => setUnreadable(true)} className="w-full max-h-64 rounded-lg border" />
            <p className="text-xs text-muted-foreground">Click the player, then: I sets start, O sets end, Left/Right move one frame, Shift+Left/Right move 1 second (30 fps assumed).</p>
          </div>
          {unreadable && <p role="alert" className="text-sm text-destructive">Couldn't read this video file. It may be damaged or in a format your browser can't open. Nothing was used from your trial. Try another file, or re-export it from the app that made it.</p>}
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-medium">Cut type:</span>
            <label className="flex items-center gap-1.5"><input type="radio" name="trim-mode" checked={mode === "exact"} onChange={() => setMode("exact")} /> Exact (recommended)</label>
            <label className="flex items-center gap-1.5"><input type="radio" name="trim-mode" checked={mode === "fast"} onChange={() => setMode("fast")} /> Fast (no re-encode, may start on the nearest key frame)</label>
          </div>
          {duration && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Video length: {formatTime(duration)} ({duration.toFixed(1)} seconds). Times can be typed as seconds (65.5), minutes:seconds (1:05.5) or hours:minutes:seconds.</p>
              <div ref={barRef} className="relative h-6 rounded bg-muted my-2 mx-2" role="group" aria-label="Selected parts of the video. Drag the handles, or focus one and use the arrow keys.">
                {segments.map((sg, si) => {
                  const a = parseTime(sg.start), b = parseTime(sg.end);
                  if (!(b > a)) return null;
                  const l = timeToPercent(a, duration), w = Math.max(0.5, timeToPercent(Math.min(b, duration), duration) - l);
                  const handle = (which: "start" | "end", pos: number, val: number) => (
                    <div
                      role="slider" tabIndex={0} aria-label={`Segment ${si + 1} ${which}`} aria-valuemin={0} aria-valuemax={Math.round(duration * 10) / 10} aria-valuenow={Math.round(val * 10) / 10} aria-valuetext={formatTime(val)}
                      className={`absolute top-[-4px] bottom-[-4px] w-3 -ml-1.5 rounded bg-primary border border-background cursor-ew-resize focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${busy ? "opacity-50" : ""}`}
                      style={{ left: `${pos}%`, touchAction: "none" }}
                      onPointerDown={(e) => onHandlePointer(e, sg.id, which)} onPointerMove={(e) => onHandlePointer(e, sg.id, which)}
                      onKeyDown={(e) => onHandleKey(e, sg.id, which)} onFocus={() => setActiveId(sg.id)}
                    />
                  );
                  return (
                    <div key={sg.id}>
                      <div className={`absolute top-0 bottom-0 ${sg.id === activeSeg?.id ? "bg-primary/70" : "bg-primary/40"}`} style={{ left: `${l}%`, width: `${w}%` }} />
                      {handle("start", l, a)}
                      {handle("end", Math.min(100, l + w), Math.min(b, duration))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {segments.map((seg, i) => (
              <div key={seg.id} className="p-3 rounded-lg border bg-card space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-muted-foreground">Segment {i + 1}</p>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs gap-1" onClick={() => setLoopId(loopId === seg.id ? null : seg.id)} disabled={busy || !!segmentError(seg)} aria-pressed={loopId === seg.id}>
                      <Repeat className="h-3 w-3" /> {loopId === seg.id ? "Stop loop" : "Loop preview"}
                    </Button>
                    {segments.length > 1 && (<>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSegment(i, -1)} disabled={busy || i === 0} aria-label={`Move segment ${i + 1} up`} title="Move up"><ChevronUp className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSegment(i, 1)} disabled={busy || i === segments.length - 1} aria-label={`Move segment ${i + 1} down`} title="Move down"><ChevronDown className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeSegment(seg.id)} disabled={busy} aria-label={`Remove segment ${i + 1}`} title="Remove segment">
                        <X className="h-3 w-3" />
                      </Button>
                    </>)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Start (seconds or m:ss)</Label><Input inputMode="decimal" aria-label={`Segment ${i + 1} start`} aria-invalid={segmentError(seg) ? true : undefined} value={seg.start} onChange={e => updateSegment(seg.id, "start", e.target.value)} disabled={busy} />
                    <Button variant="ghost" size="sm" className="h-6 px-1 text-xs mt-1" onClick={() => setFromPlayer(seg.id, "start")} disabled={busy}>Set start to player time</Button></div>
                  <div><Label className="text-xs">End (seconds or m:ss)</Label><Input inputMode="decimal" aria-label={`Segment ${i + 1} end`} aria-invalid={segmentError(seg) ? true : undefined} value={seg.end} onChange={e => updateSegment(seg.id, "end", e.target.value)} disabled={busy} />
                    <Button variant="ghost" size="sm" className="h-6 px-1 text-xs mt-1" onClick={() => setFromPlayer(seg.id, "end")} disabled={busy}>Set end to player time</Button></div>
                </div>
                {segmentError(seg) && <p role="alert" className="text-xs text-destructive">{segmentError(seg)}</p>}
                {seg.result && seg.cutMode === "fast" && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{actualStartText(parseTime(seg.start), seg.earlyBy, formatTime)}</span>
                    <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => recutExact(seg.id)} disabled={busy}>{recutting === seg.id ? "Re-cutting..." : "Re-cut exactly"}</Button>
                  </div>
                )}
                {seg.result && (
                  <DownloadButton blob={seg.result!} fileName={`segment${i + 1}_${file.name.replace(/\.[^.]+$/, "")}.${mode === "exact" || seg.result.type === "video/mp4" ? "mp4" : (file.name.match(/\.([^.]+)$/)?.[1] ?? "mp4")}`} label={`Download Segment ${i + 1}`} />
                )}
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={addSegment} disabled={busy} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Segment
          </Button>

          {busy && <VideoProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}

          <p className="text-xs text-muted-foreground" data-testid="trim-output-label">{outputLabel(mode, file.name.match(/\.([^.]+)$/)?.[1] || "mp4")}. Merged clips are always MP4, in the order listed above.</p>
          <div className="flex flex-wrap gap-3 pt-2 border-t">
            <Button onClick={processAllSegments} disabled={busy || sizeCheck?.severity !== "ok" || anySegmentInvalid || unreadable} className="gap-1.5">
              <Scissors className="h-3.5 w-3.5" /> {processing ? "Trimming..." : "Trim All Segments"}
            </Button>
            {allProcessed && segments.length >= 2 && !mergedResult && (
              <Button variant="outline" onClick={mergeAndDownload} disabled={busy}>{merging ? "Merging..." : "Merge segments into one clip"}</Button>
            )}
            {mergedResult && (
              <DownloadButton blob={mergedResult} fileName={`merged_${file.name.replace(/\.[^.]+$/, "")}.mp4`} label="Download Merged Video" />
            )}
            <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setSegments([{ id: 0, start: "0", end: "10", result: null }]); setMergedResult(null); }} disabled={busy}>New</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
